/* =====================================================================================
   Laboratory technical profile.

   What a laboratory RUNS, which decides whether they can adopt an assay at all. A unit with
   no long-read sequencer cannot run a nanopore assay next month whatever their transplant
   volume says, and a laboratory already doing NGS HLA typing on Illumina has a switching
   cost a volume model cannot see.

   Deliberately empty until somebody who has been in the building fills it in. A rep who
   arrives believing a laboratory owns a MiSeq it does not own has lost the room in the first
   five minutes, so every field records its source and the date it was observed.
   ===================================================================================== */

import { BACKEND } from './db';
import { dbSiteId } from './ids';
import { explain } from './dberrors';

/* Per-method vendor and commitment. A laboratory runs several methods, each with its own
   incumbent and its own end date, so this is a list rather than fields on the parent. */
export interface MethodVendor {
  id: string; site_id: string; method: string;
  vendor: string | null; product: string | null; platform: string | null;
  commitment: string | null; commitment_ref: string | null;
  starts_on: string | null; ends_on: string | null; notice_months: number | null;
  /** End date less notice. The date the account can actually be sold to. */
  addressable_from: string | null;
  annual_value: number | null; currency: string | null;
  satisfaction: string | null; displaceable: string | null;
  notes: string | null; confidence: string;
  /* Provenance. Present on the table since 0033 and missing from this interface, which is why
     the save could not set them without a type error. */
  source: string | null; observed_on: string | null; recorded_by: string | null;
}

export interface AuditEntry {
  id: number; field: string; old_value: string | null; new_value: string | null;
  changed_at: string; changed_by: string | null; rationale: string | null;
}

export const CFDNA_TUBES = [
  { key: 'streck',       label: 'Streck Cell-Free DNA BCT' },
  { key: 'pacbio',       label: 'PacBio / Norgen cf-DNA tube' },
  { key: 'roche_ccfdna', label: 'Roche cell-free DNA collection tube' },
  { key: 'norgen',       label: 'Norgen cf-DNA preservative tube' },
  { key: 'edta',         label: 'EDTA (K2/K3)' },
  { key: 'other',        label: 'Other (describe)' },
  { key: 'unknown',      label: 'Not known' },
] as const;

export const METHODS = [
  { key: 'hla_typing_low',  label: 'HLA typing, low resolution' },
  { key: 'hla_typing_high', label: 'HLA typing, high resolution' },
  { key: 'hla_typing_ngs',  label: 'HLA typing, NGS' },
  { key: 'antibody_screening', label: 'Antibody screening' },
  { key: 'antibody_single_antigen', label: 'Single antigen bead' },
  { key: 'crossmatch_flow', label: 'Crossmatch, flow' },
  { key: 'crossmatch_cdc',  label: 'Crossmatch, CDC' },
  { key: 'chimerism_str',   label: 'Chimerism, STR' },
  { key: 'chimerism_qpcr',  label: 'Chimerism, qPCR' },
  { key: 'chimerism_ddpcr', label: 'Chimerism, digital PCR' },
  { key: 'chimerism_ngs',   label: 'Chimerism, NGS' },
  { key: 'ddcfdna',         label: 'Donor-derived cfDNA' },
  { key: 'biopsy_molecular', label: 'Molecular biopsy' },
  { key: 'gene_expression', label: 'Gene expression' },
  { key: 'dna_extraction',  label: 'DNA extraction' },
  { key: 'cfdna_extraction', label: 'cfDNA extraction' },
  { key: 'sequencing',      label: 'Sequencing' },
  { key: 'digital_pcr',     label: 'Digital PCR' },
  { key: 'lims',            label: 'LIMS' },
  { key: 'other',           label: 'Other' },
] as const;

export const COMMITMENTS = ['tender', 'framework', 'study', 'trial', 'evaluation',
                            'rolling', 'none', 'unknown'] as const;

export interface Technical {
  id?: string;
  site_id: string;
  /* Pre-analytical. For cfDNA the tube IS the assay: a stabilised tube holds cells for days,
     EDTA gives about six hours before genomic background swamps the donor fraction. */
  cfdna_tube: string | null;
  cfdna_tube_other: string | null;
  plasma_input_ml: number | null;
  plasma_separation_hrs: number | null;
  cfdna_extraction_kit: string | null;
  accreditations: string[];
  accreditation_ref: string | null;
  hla_typing_methods: string[];
  hla_resolution: string | null;
  antibody_methods: string[];
  chimerism_method: string | null;
  does_ddcfdna: boolean | null;
  does_biopsy_molecular: boolean | null;
  dna_extraction: string[];
  extraction_automated: boolean | null;
  thermocyclers: string[];
  sequencers: string[];
  has_long_read: boolean | null;
  flow_cytometers: string[];
  luminex_platform: string | null;
  qpcr_platforms: string[];
  digital_pcr: string | null;
  liquid_handlers: string[];
  other_equipment: string[];
  lims: string | null;
  hla_software: string[];
  staffing_wte: number | null;
  operates_out_of_hours: boolean | null;
  turnaround_hours_routine: number | null;
  turnaround_hours_urgent: number | null;
  annual_typings: number | null;
  annual_crossmatches: number | null;
  send_out_tests: string[];
  capacity_note: string | null;
  source: string | null;
  observed_on: string | null;
  confidence: string;
  notes: string | null;
  updated_at?: string;
}

/* Suggestions only. The stored value is free text, because instrument names change every
   year and a dropdown that has to be migrated before a rep can record what they saw simply
   will not be used. */
export const SUGGEST = {
  accreditations: ['EFI', 'ASHI', 'UKAS', 'CAP', 'ISO 15189', 'CLIA'],
  hla_typing_methods: ['SSO', 'SSP', 'SBT', 'NGS', 'real-time PCR', 'qPCR'],
  antibody_methods: ['Luminex SAB', 'flow crossmatch', 'CDC crossmatch', 'ELISA'],
  dna_extraction: ['QIAsymphony SP', 'QIAcube', 'Maxwell RSC', 'chemagic 360', 'manual column', 'salting out'],
  thermocyclers: ['Veriti', 'ProFlex', 'T100', 'C1000 Touch', 'Mastercycler'],
  sequencers: ['MiSeq', 'MiniSeq', 'iSeq 100', 'NextSeq 550', 'NextSeq 1000', 'Ion GeneStudio',
               'MinION', 'GridION', 'PromethION', 'Sequel II', 'Revio'],
  flow_cytometers: ['FACSCanto II', 'FACSLyric', 'Navios', 'CytoFLEX'],
  qpcr_platforms: ['QuantStudio 5', 'QuantStudio 7', 'CFX96', 'LightCycler 480', 'Rotor-Gene Q'],
  liquid_handlers: ['Hamilton STAR', 'Tecan Fluent', 'epMotion', 'Biomek i7'],
  hla_software: ['HLA Fusion', 'NGSengine', 'Assign TruSight', 'TypeStream Visual', 'SCORE 6', 'HistoTrac'],
} as const;

/* Long-read is the field that changes a sale from "buy an instrument" to "run it next week",
   so it is derived from the sequencer list rather than left to be ticked separately. */
const LONG_READ = /minion|gridion|promethion|sequel|revio|pacbio|nanopore|ont/i;
export const impliesLongRead = (sequencers: string[]) => sequencers.some((s) => LONG_READ.test(s));

const live = () => BACKEND === 'supabase';

export async function fetchTechnical(siteId: string): Promise<Technical | null> {
  if (!live()) return null;
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase()
    .schema('atlas').from('site_technical').select('*').eq('site_id', dbSiteId(siteId)).maybeSingle();
  if (error) throw new Error(explain(error.message, 'save the technical record'));
  return data as Technical | null;
}

export async function saveTechnical(siteId: string, t: Partial<Technical>) {
  const { supabase, currentMember } = await import('./supabase');
  const me = await currentMember();
  if (!me?.id) throw new Error('No member record for the signed-in user.');
  const { data: org } = await supabase().schema('app').from('orgs').select('id').limit(1).maybeSingle();

  const payload = {
    ...t,
    org_id: org?.id,
    site_id: dbSiteId(siteId),
    // Derived rather than asked for: if they listed a nanopore box, they have long read.
    has_long_read: t.sequencers ? impliesLongRead(t.sequencers) : t.has_long_read ?? null,
    recorded_by: me.id,
    observed_on: t.observed_on || new Date().toISOString().slice(0, 10),
  };
  const { stripGenerated } = await import('./supabase');
  const { error } = await supabase().schema('atlas').from('site_technical')
    .upsert(stripGenerated('atlas.site_technical', payload), { onConflict: 'site_id' });
  if (error) {
    if (error.code === '42501') throw new Error('Refused by row level security.');
    throw new Error(explain(error.message, 'save the technical record'));
  }
}

/** Parse a comma-separated box into a clean array without empty entries. */
export const toList = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);
export const fromList = (a: string[] | null | undefined) => (a ?? []).join(', ');


/* ---------------------------------------------------------------------------------
   Per-method vendors, and the audit trail.
   --------------------------------------------------------------------------------- */

export async function fetchMethodVendors(siteId: string): Promise<MethodVendor[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase().schema('atlas')
    .from('site_method_vendors').select('*')
    .eq('site_id', dbSiteId(siteId)).order('method');
  if (error) throw new Error(explain(error.message, 'load the vendor list'));
  return (data ?? []) as MethodVendor[];
}

export async function saveMethodVendor(siteId: string, v: Partial<MethodVendor>) {
  const { supabase, currentMember } = await import('./supabase');

  /* org_id WAS MISSING HERE, and the consequence was not a missing-field error.

     The row level security check on this table is app.can_write(org_id). With org_id null the
     test evaluates null against the caller's organisation, which is not true, so Postgres
     refused the insert as a policy violation. The client then translated that refusal into
     "you do not have permission, ask an owner to check your roles in Crew", which sent a user
     who held every role she needed to go and ask for roles she already had.

     A missing column presenting as a permissions failure is the worst kind of error message:
     it is confident, it is actionable, and it points at the wrong thing entirely. Set here the
     same way saveTechnical does it, and the record now carries who entered it. */
  const me = await currentMember();
  const { data: org } = await supabase().schema('app').from('orgs')
    .select('id').limit(1).maybeSingle();
  if (!org?.id) throw new Error('Could not resolve your organisation. Sign in again.');

  /* stripGenerated, because addressable_from is GENERATED ALWAYS and an edit sends back the
     whole row it just read, including the column the database computed. Postgres then refuses
     with "cannot insert a non-default value", which fails on the second save and never on the
     first. Applied centrally so the other seven generated columns cannot bite either. */
  const { stripGenerated } = await import('./supabase');
  const { error } = await supabase().schema('atlas').from('site_method_vendors')
    .upsert(stripGenerated('atlas.site_method_vendors', {
      ...v,
      org_id: org.id,
      site_id: dbSiteId(siteId),
      recorded_by: me?.id ?? null,
      observed_on: v.observed_on || new Date().toISOString().slice(0, 10),
    }), { onConflict: 'id' });
  if (error) throw new Error(explain(error.message, 'save this vendor record'));
}

/** Who changed what, when. Written by database trigger, so it cannot be skipped. */
export async function fetchTechnicalAudit(siteId: string): Promise<AuditEntry[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase().schema('atlas')
    .from('technical_audit').select('*')
    .eq('site_id', dbSiteId(siteId)).order('changed_at', { ascending: false }).limit(100);
  if (error) throw new Error(explain(error.message, 'load the change history'));
  return (data ?? []) as AuditEntry[];
}


/* ---------------------------------------------------------------------------------
   Corrections to records already written.

   Both tables carry the zz_audit trigger, so the before and after rows and the actor are
   recorded by the database on every update. Nothing here needs to log anything, and nothing
   here could be trusted to.
   --------------------------------------------------------------------------------- */

export async function updateIntelligence(id: string, patch: Record<string, unknown>) {
  const { supabase, stripGenerated } = await import('./supabase');
  const { error } = await supabase().schema('atlas').from('intelligence')
    .update(stripGenerated('atlas.intelligence', patch)).eq('id', id);
  if (error) throw new Error(explain(error.message, 'save this correction'));
}

export async function updateInteraction(id: string, patch: Record<string, unknown>) {
  const { supabase, stripGenerated } = await import('./supabase');
  const { error } = await supabase().schema('crm').from('interactions')
    .update(stripGenerated('crm.interactions', patch)).eq('id', id);
  if (error) throw new Error(explain(error.message, 'save this correction'));
}
